import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';

const ContactPage = ({ language }) => {
  // Try to load user data from localStorage
  const storedUser = localStorage.getItem('user');
  let userData = null;
  if (storedUser) {
    try {
      userData = JSON.parse(storedUser);
    } catch (e) {
      console.error('Error parsing user from localStorage', e);
    }
  }

  // Check URL query parameters
  const queryParams = new URLSearchParams(window.location.search);
  const isShradhdham = queryParams.get('service') === 'shradhdham';
  
  const defaultMessage = isShradhdham ? 
    `<p>Dear Sir,</p><p>I saw your advertisement related to Annual Shradham done satisfactorily for pitrus at your place with moderate cost. So I wish to book for a shradham of my ..................... on the selected date.</p>` : '';

  const [formData, setFormData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    subject: isShradhdham ? 'Booking for Annual Shradham' : '',
    message: defaultMessage,
    preferredDate: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
  const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

  // Initialize Quill editor
  useEffect(() => {
    if (editorRef.current && !quillRef.current && window.Quill) {
      const toolbarOptions = [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'color': [] }],
        ['link'],
        ['clean']
      ];

      quillRef.current = new window.Quill(editorRef.current, {
        theme: 'snow',
        modules: { toolbar: toolbarOptions },
        placeholder: language === 'en' ? 'Write your message here...' : 'உங்கள் செய்தியை இங்கே எழுதுங்கள்...'
      });
      
      // Set initial message if prepopulated
      if (defaultMessage) {
        quillRef.current.clipboard.dangerouslyPasteHTML(defaultMessage);
      }

      // Update formData when content changes
      quillRef.current.on('text-change', () => {
        setFormData(prev => ({
          ...prev,
          message: quillRef.current.root.innerHTML
        }));
      });
    }
  }, [language, defaultMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Get content from Quill editor before submitting
    const messageContent = quillRef.current ? quillRef.current.root.innerHTML : formData.message;
    
    if (!recaptchaToken) {
      setError(language === 'en' ? 'Please complete the reCAPTCHA verification.' : 'reCAPTCHA சரிபார்ப்பை முடிக்கவும்');
      setLoading(false);
      return;
    }
    
    // Strip HTML if only plain text (no formatting)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = messageContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Use plain text if no HTML formatting, otherwise use HTML
    const finalMessage = messageContent.includes('<') ? messageContent : textContent;
    
    const submitData = {
      ...formData,
      message: (formData.preferredDate ? `<p><strong>Preferred Date:</strong> ${formData.preferredDate}</p><br/>` : '') + (finalMessage || textContent),
      recaptcha_token: recaptchaToken
    };

    console.log('📧 Submitting to:', `${API}/contact/submit`, submitData);

    try {
      const response = await axios.post(`${API}/contact/submit`, submitData);
      console.log('✅ Contact form response:', response.data);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setRecaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (err) {
      console.error('❌ Contact form error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.detail || (language === 'en' ? 'Failed to send message. Please try again.' : 'செய்தியை அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும்.'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-3xl font-bold text-green-800 mb-4">
              {language === 'en' ? 'Message Sent Successfully!' : 'செய்தி வெற்றிகரமாக அனுப்பப்பட்டது!'}
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              {language === 'en'
                ? 'Thank you for contacting us. We will get back to you soon!'
                : 'எங்களை தொடர்பு கொண்டதற்கு நன்றி. நாங்கள் விரைவில் உங்களை தொடர்பு கொள்வோம்!'}
            </p>
            
            {/* Quick Navigation */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <a
                href="/"
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition"
              >
                🏠 {language === 'en' ? 'Home' : 'முகப்பு'}
              </a>
              <a
                href="/blog"
                className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 font-semibold transition"
              >
                📝 {language === 'en' ? 'Blog' : 'வலைப்பதிவு'}
              </a>
              <a
                href="/shop"
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold transition"
              >
                🛒 {language === 'en' ? 'Shop' : 'கடை'}
              </a>
            </div>
            
            <button
              onClick={() => setSubmitted(false)}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 font-medium transition text-sm"
            >
              {language === 'en' ? 'Send Another Message' : 'மற்றொரு செய்தியை அனுப்பவும்'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-green-800 mb-4">
            {language === 'en' ? 'Contact Us' : 'எங்களை தொடர்பு கொள்ளுங்கள்'}
          </h1>
          <p className="text-xl text-gray-700">
            {language === 'en'
              ? 'Have questions? We\'d love to hear from you!'
              : 'கேள்விகள் உள்ளதா? உங்களிடமிருந்து கேட்க நாங்கள் விரும்புகிறோம்!'}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-orange-600 mb-6">
                {language === 'en' ? 'Send us a Message' : 'எங்களுக்கு ஒரு செய்தி அனுப்பவும்'}
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-100 text-red-800 border border-red-200 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'en' ? 'Your Name *' : 'உங்கள் பெயர் *'}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'en' ? 'Email Address *' : 'மின்னஞ்சல் முகவரி *'}
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'en' ? 'Phone Number' : 'தொலைபேசி எண்'}
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'en' ? 'Preferred Date (Optional)' : 'விருப்பமான தேதி (விருப்பத்தேர்வு)'}
                    </label>
                    <input
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => setFormData({...formData, preferredDate: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Subject *' : 'தலைப்பு *'}
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 mb-6"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Your Message *' : 'உங்கள் செய்தி *'}
                  </label>
                  <div 
                    ref={editorRef}
                    style={{ height: '250px', background: 'white' }}
                    className="border rounded-lg focus-within:ring-2 focus-within:ring-green-500"
                  ></div>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="hidden"
                    rows="6"
                    required
                  />
                </div>

                <div className="my-4">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcHtHctAAAAAEBv7fRnOnUWot2HppbIt6-K6UE4'}
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-600 to-green-600 text-white py-4 rounded-lg hover:from-orange-700 hover:to-green-700 font-bold text-xl disabled:opacity-50 transition"
                >
                  {loading ? (language === 'en' ? 'Sending...' : 'அனுப்புகிறது...') : (language === 'en' ? 'Send Message' : 'செய்தியை அனுப்பவும்')}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-8">
              <h3 className="text-2xl font-bold text-green-800 mb-6">
                {language === 'en' ? 'Contact Information' : 'தொடர்பு தகவல்'}
              </h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">📧</span>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {language === 'en' ? 'Email' : 'மின்னஞ்சல்'}
                    </h4>
                    <a href="mailto:svknvs@gmail.com" className="text-green-600 hover:text-green-700">
                      svknvs@gmail.com
                    </a>
                    <br />
                    <a href="mailto:svknvs@gmail.com" className="text-green-600 hover:text-green-700">
                      svknvs@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🕐</span>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {language === 'en' ? 'Response Time' : 'பதில் நேரம்'}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {language === 'en'
                        ? 'We typically respond within 24-48 hours'
                        : 'நாங்கள் பொதுவாக 24-48 மணி நேரத்திற்குள் பதிலளிப்போம்'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🌐</span>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {language === 'en' ? 'Website' : 'இணையதளம்'}
                    </h4>
                    <a href="https://ahobilam.com" className="text-green-600 hover:text-green-700">
                      www.ahobilam.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  {language === 'en'
                    ? '💡 For urgent matters, please include "URGENT" in your subject line.'
                    : '💡 அவசர விஷயங்களுக்கு, தயவுசெய்து உங்கள் தலைப்பு வரியில் "அவசரம்" என்று சேர்க்கவும்.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
